/**
 * The canonical "which day did this work happen on" rule.
 *
 * `./work-sessions` pairs a run of punches into sessions and deliberately
 * leaves day grouping to its callers. That was the wrong boundary: the four
 * callers picked three different day keys and all but one of them bucketed
 * RAW PUNCHES before pairing, so a clock-in at 23:00 and its clock-out at
 * 02:00 landed in different buckets, each half lost its pair, and the shift
 * reported zero hours.
 *
 * The rules the callers used before this module existed:
 *
 *  - api-time timesheet.utils: `punch.punchDate`, which create-punch derives
 *    as `new Date(timestamp).toISOString().split('T')[0]` — a UTC date. For
 *    US timezones that splits an ORDINARY evening shift, not just a graveyard
 *    one: 18:00 America/Chicago is already the next day in UTC.
 *  - api-payroll get-period: `punch.punchDate || punch.timestamp.split('T')[0]`
 *    — the same UTC date, falling back to the literal text before the 'T',
 *    which is the OFFSET-local date for a timestamp written as
 *    `...T20:00:00-05:00` and the UTC date for one written with `Z`. Two
 *    different rules depending on how the row happened to be serialized.
 *  - apps/web punch-history and TimesheetCalendar: the VIEWER's browser
 *    timezone, so the same punch moved between days depending on who opened
 *    the screen and from where.
 *
 * All four now resolve a day through `localDayKey` in the TENANT's timezone,
 * and all four pair before attributing. A session is credited to the day it
 * STARTED (`sessionDayKey`) — the day the employee would name if you asked
 * them when they worked that shift, and the day the overtime rules in
 * api-time and api-payroll already assume when they cap a "day" at 8h.
 *
 * Because the tenant-local day is not the UTC day, a caller fetching one pay
 * period must fetch a day wider on each side and filter AFTER attributing —
 * see `shiftDayKey`. Fetching exactly `[startDate, endDate]` by UTC-derived
 * `punchDate` silently dropped the last local evening of every period.
 */
import { isSupportedTimeZone, shiftIsoDate, tenantLocalDate } from './tenant-time';
import { sumWorkSessions } from './work-sessions';
/**
 * Timezone assumed when a tenant record carries none.
 *
 * Matches the default in `packages/schemas/src/signup.ts`, so a tenant that
 * predates the signup form's timezone step is read the same way a tenant that
 * accepted the default is. Tenants seeded outside signup have no `timezone`
 * at all and land here.
 */
export const DEFAULT_TENANT_TIMEZONE = 'America/Chicago';
/**
 * Longest session any hours view will credit, in hours.
 *
 * Pairing before day attribution is what fixes the split-shift bug, but it
 * also removes a bound the old per-day bucketing gave for free: a missed
 * `clock_out` used to leave two orphaned halves in different buckets, scoring
 * zero, whereas pairing across a period lets that clock-in reach forward to
 * the NEXT clock-out. Measured on the shipped code, a clock-in on 2026-07-01
 * with the next clock-out on 2026-07-13 billed 276 hours, 268 of them as
 * overtime.
 *
 * 16h is a double shift — the longest thing anyone actually works here, and a
 * third longer than the longest session in production (11.97h). It was 24h,
 * which is the bound on a *calendar day* rather than on a shift, and that gap
 * is where the damage lived: a clock-in at 09:00 paired with the next
 * morning's 08:00 clock-out billed 23h in full, 15 of them at overtime rate,
 * where the old per-day bucketing billed 0. Since a second `clock_in` is
 * rejected while a session is open, punching out the next morning is the only
 * move the system leaves an employee who forgot — so that shape is the normal
 * case for a missed punch, not a corner case.
 *
 * Sessions past the cap are DROPPED, not clamped — see `maxSessionHours` in
 * ./work-sessions for why. Dropping is only safe because it is VISIBLE:
 * callers pass `onOverCap` and report what was dropped, so a missed punch
 * shows up as something to correct instead of as a silent zero. A caller that
 * sets the cap without that callback is hiding the thing the cap detects.
 */
export const MAX_SESSION_HOURS = 16;
/**
 * Coerce a stored timezone into one `Intl` will accept.
 *
 * A tenant's `timezone` is free-form text in DynamoDB — it is never validated
 * on write — so an unrecognized or empty value must not throw inside an hours
 * calculation. Anything `Intl` rejects falls back to
 * `DEFAULT_TENANT_TIMEZONE`, which keeps a bad record reporting plausible
 * hours in the tenant's likely zone rather than failing the request.
 */
export function resolveTimeZone(timeZone) {
    return isSupportedTimeZone(timeZone) ? timeZone : DEFAULT_TENANT_TIMEZONE;
}
/**
 * The tenant-local calendar date an instant falls on.
 *
 * Thin wrapper over `tenantLocalDate`, which owns the `Intl` mechanics. Two
 * differences, both for hours callers: this accepts a timestamp STRING as well
 * as a Date, because punch rows carry strings; and it COERCES an unusable zone
 * through `resolveTimeZone` instead of throwing, because an hours calculation
 * must still return a number for a tenant whose record is incomplete.
 */
export function localDayKey(instant, timeZone) {
    const date = typeof instant === 'string' ? new Date(instant) : instant;
    return tenantLocalDate(date, resolveTimeZone(timeZone));
}
/**
 * The day a completed session is credited to: the day it STARTED.
 *
 * An 23:00–02:00 shift is one 3h session on the day of the clock-in, not
 * 1h on one day and 2h on the next. Splitting it at midnight would be the
 * other defensible rule, but it would also split the 8h daily-overtime cap
 * across two days and turn a single overnight shift into two short ones — and
 * it is not what any caller did before.
 *
 * Note this is a CHANGE for TimesheetCalendar, which credited the clock-out
 * day. An overnight shift moves one day earlier on that calendar; a shift
 * that ends on the same day it started is unaffected, which is nearly all of
 * them.
 */
export function sessionDayKey(session, timeZone) {
    return localDayKey(session.clockIn, timeZone);
}
/** Net hours for one session: paired time minus its completed breaks. */
export function sessionNetHours(session) {
    const { workMs, breakMs } = sumWorkSessions([session]);
    return (workMs - breakMs) / 3_600_000;
}
/**
 * Bucket completed sessions by the tenant-local day each one started.
 *
 * Callers pair the WHOLE range they fetched and then group here, which is the
 * inversion this module exists for. Days with no session are absent rather
 * than zero; callers that render a full calendar seed their own days.
 */
export function groupSessionsByDay(sessions, timeZone) {
    const zone = resolveTimeZone(timeZone);
    const byDay = new Map();
    for (const session of sessions) {
        const key = sessionDayKey(session, zone);
        const bucket = byDay.get(key);
        if (bucket)
            bucket.push(session);
        else
            byDay.set(key, [session]);
    }
    return byDay;
}
/**
 * Net hours per tenant-local day. The shape the overtime rules want.
 */
export function netHoursByDay(sessions, timeZone) {
    const totals = new Map();
    for (const [day, daySessions] of groupSessionsByDay(sessions, timeZone)) {
        // Accumulated per session, in hours, deliberately — api-payroll depends on
        // this arithmetic: summing milliseconds and dividing once changes the last
        // bit and paid hours are rounded to 2dp, enough to move a period total by
        // 0.01h. See the note in api-payroll get-period.
        let hours = 0;
        for (const session of daySessions)
            hours += sessionNetHours(session);
        totals.set(day, hours);
    }
    return totals;
}
/**
 * Move a `YYYY-MM-DD` key by whole days.
 *
 * Delegates to `shiftIsoDate`: a day key is a label, not an instant, so
 * `2026-03-08` + 1 is `2026-03-09` whether or not that night lost an hour.
 * Used to widen a fetch window, because the tenant-local day at the edge of a
 * pay period reaches into the neighbouring UTC day.
 */
export function shiftDayKey(dayKey, days) {
    return shiftIsoDate(dayKey, days);
}
/**
 * The UTC-keyed window to fetch so that every punch belonging to the
 * tenant-local range `[startDate, endDate]` is included.
 *
 * One day of slack each side covers every real IANA offset (UTC-12..UTC+14)
 * — the local day can only reach into the immediately neighbouring UTC day.
 * Callers filter the surplus out after attributing sessions to days.
 */
export function fetchDayRange(startDate, endDate) {
    return {
        fetchStart: shiftDayKey(startDate, -1),
        fetchEnd: shiftDayKey(endDate, 1),
    };
}
/** Inclusive `YYYY-MM-DD` range check, by string compare. */
export function isDayKeyInRange(dayKey, startDate, endDate) {
    return dayKey >= startDate && dayKey <= endDate;
}
